import React, { useState } from 'react';
import { z } from 'zod';
import { Bell } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@/hooks/useTranslation';
import { useFxRatePerEur } from '@/hooks/useFxRatePerEur';
import { trackListingWatchSubmit } from '@/utils/buyerJourneyTracking';

interface Props {
  listingId: string;
  make?: string;
  model?: string;
  currentPrice: number;
  currency?: string | null;
}

const emailSchema = z.string().trim().email('Email invalid').max(255);

export const ListingPriceAlertCard: React.FC<Props> = ({ listingId, make, model, currentPrice, currency }) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [wantWatch, setWantWatch] = useState(true);
  const [wantSimilar, setWantSimilar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const cur = (currency || 'EUR').toUpperCase();
  const { data: rate } = useFxRatePerEur(cur);
  // Alert threshold is always EUR (fx_rates); hidden when a non-EUR listing has no rate.
  const priceEur = cur === 'EUR' ? currentPrice : rate ? currentPrice / rate : null;

  const submit = async () => {
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast({ title: 'Email invalid', variant: 'destructive' });
      return;
    }
    if (!wantWatch && !wantSimilar) return;
    setBusy(true);
    try {
      if (wantWatch) {
        const { data, error } = await supabase.functions.invoke('cron-price-alerts-scan', {
          body: { action: 'watch_subscribe', email: parsed.data, listing_id: listingId },
        });
        if (error || !data?.status) throw new Error(t('price_alert_card.error'));
      }
      if (wantSimilar) {
        const { data, error } = await supabase.functions.invoke('cron-price-alerts-scan', {
          body: { action: 'subscribe', email: parsed.data, make: make || null, model: model || null, price_max: Math.round(priceEur ?? 0) },
        });
        if (error || !data?.status) throw new Error(t('price_alert_card.error'));
      }
      trackListingWatchSubmit(wantWatch, wantSimilar);
      setDone(true);
      toast({ title: t('price_alert_card.check_email_title'), description: t('price_alert_card.check_email_description') });
    } catch (err: any) {
      toast({ title: 'Nu am putut activa', description: err?.message || 'Reîncearcă', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  if (!priceEur) return null;

  return (
    <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Bell className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1">
          <h4 className="font-medium text-foreground text-sm">{t('price_alert_card.title')}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{t('price_alert_card.description')}</p>

          {done ? (
            <p className="mt-3 text-xs text-brand-green">{t('price_alert_card.check_email_title')}</p>
          ) : !open ? (
            <Button variant="outline" size="sm" className="mt-3" onClick={() => setOpen(true)}>
              {t('price_alert_card.cta')}
            </Button>
          ) : (
            <div className="mt-3 space-y-3">
              <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                <Checkbox checked={wantWatch} onCheckedChange={(v) => setWantWatch(v === true)} className="mt-0.5" />
                <span>{t('price_alert_card.option_watch')}</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                <Checkbox checked={wantSimilar} onCheckedChange={(v) => setWantSimilar(v === true)} className="mt-0.5" />
                <span>{t('price_alert_card.option_similar')}</span>
              </label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="email@exemplu.ro"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9 text-sm"
                />
                <Button size="sm" onClick={submit} disabled={busy || (!wantWatch && !wantSimilar)}>
                  {busy ? '...' : 'OK'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
